import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, LogOut } from 'lucide-react';
import { useMyAccount } from '../../../../core/auth/context/MyAccountContext';
import { useLogout } from '../../../../core/hooks/useLogout';
import { API } from '../../../../core/config';

const Avatar: React.FC<{ src: string | null; name: string; initials: string }> = ({
  src,
  name,
  initials,
}) => {
  return (
    <div className="w-9 h-9 rounded-full overflow-hidden bg-gradient-to-br from-primary to-accent shadow-lg shadow-primary/30 flex items-center justify-center text-white font-bold text-sm transition-transform duration-200 hover:scale-105">
      {src ? (
        <img src={src} alt={name} crossOrigin="anonymous" className="w-full h-full object-cover" />
      ) : (
        initials
      )}
    </div>
  );
};

const ProfileDropdown: React.FC = () => {
  const { data, loading } = useMyAccount();
  const { logout } = useLogout();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const profile = data?.profile?.user as any;
  const displayName = profile?.display_name || profile?.username || 'Utilisateur';
  const email = data?.emails?.find((item) => item.is_primary === true)?.email || '';
  const avatarUrl = profile?.avatar_url || null;
  const avatar = avatarUrl
    ? avatarUrl.startsWith('http')
      ? avatarUrl
      : `${API.STORAGE_BASE_URL}${avatarUrl}`
    : null;
  const initials = displayName.charAt(0).toUpperCase();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent/10 transition-colors"
      >
        <Avatar src={avatar} name={displayName} initials={initials} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-2xl shadow-gray-500/30 border border-gray-200 overflow-hidden z-50">
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <Avatar src={avatar} name={displayName} initials={initials} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-800 truncate">{displayName}</p>
                <p className="text-xs text-gray-500 truncate">{email}</p>
              </div>
            </div>
          </div>
          <div className="py-1">
            <Link
              to="/profile"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-accent/10 hover:text-accent"
            >
              <User className="w-4 h-4 stroke-[1.5]" />
              Profil
            </Link>
            <button
              onClick={() => {
                setOpen(false);
                logout();
              }}
              className="flex items-center gap-3 w-full px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 stroke-[1.5]" />
              Déconnexion
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;